import React from "react";
import { Helmet } from "react-helmet-async";

const defaultSiteUrl = "https://utu.africa";
const defaultTitle = "UTU - The Sovereign Context Layer for AI";
const defaultDescription =
  "UTU grounds artificial intelligence in human and cultural truth so systems understand, not hallucinate. Intelligence, Rooted in Context.";
const defaultImage = `${defaultSiteUrl}/static/media/utu_hero_logo.png`;
const defaultTwitterHandle = "@UtUAfrica";

/**
 * SEO Component - Comprehensive SEO optimization with latest best practices
 * Supports Open Graph, Twitter Cards, structured data, and more
 */
export function SEO({
  title,
  description = defaultDescription,
  image = defaultImage,
  url,
  type = "website",
  siteName = "UTU",
  twitterHandle = defaultTwitterHandle,
  keywords = [
    "UTU",
    "AI",
    "artificial intelligence",
    "context layer",
    "sovereign AI",
    "AI infrastructure",
    "knowledge graphs",
    "semantic understanding",
    "cultural context",
    "AI meaning",
  ],
  structuredData,
  canonical,
  noindex = false,
  nofollow = false,
}) {
  const fullTitle = title ? `${title} | ${siteName}` : defaultTitle;
  const fullUrl = url ? `${defaultSiteUrl}${url}` : defaultSiteUrl;
  const canonicalUrl = canonical || fullUrl;

  // Robots meta
  const robotsContent = [
    noindex ? "noindex" : "index",
    nofollow ? "nofollow" : "follow",
  ].join(", ");

  // Default structured data if not provided
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "UTU",
    url: defaultSiteUrl,
    logo: defaultImage,
    description: defaultDescription,
    sameAs: [
      // Add social media links here when available
    ],
  };

  // Handle both single object and array of structured data
  const schemaData = structuredData || defaultStructuredData;
  const schemaDataArray = Array.isArray(schemaData) ? schemaData : [schemaData];

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(", ")} />
      <meta name="robots" content={robotsContent} />
      <meta name="language" content="English" />
      <meta name="author" content="UTU" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={fullTitle} />
      {twitterHandle && <meta name="twitter:creator" content={twitterHandle} />}
      {twitterHandle && <meta name="twitter:site" content={twitterHandle} />}

      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#FCD34D" />
      <meta name="msapplication-TileColor" content="#FCD34D" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />

      {/* Structured Data (JSON-LD) */}
      {schemaDataArray.map((data, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
}

/**
 * WebSite structured data for search engines
 */
export const websiteStructuredData = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "UTU",
  url: defaultSiteUrl,
  description: defaultDescription,
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${defaultSiteUrl}/search?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
};

/**
 * Organization structured data
 */
export const organizationStructuredData = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "UTU",
  url: defaultSiteUrl,
  logo: defaultImage,
  description: defaultDescription,
  foundingDate: "2024",
  sameAs: [],
};
